import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ROLL_CALL_API_BASE_URL } from "../../RollCallConfig/RollCallConfig";
import Swal from "sweetalert2";
import { Spin } from "antd";

const INITIAL_STATE = {
  Name: "",
  Code: "",
  StartTime: "",
  EndTime: "",
  StartGrace: "",
  EndGrace: "",
  ApproxStartGrace: "",
  ApproxEndGrace: "",
  WorkMins: "",
  OTCalcMins: "",
  operdays: "",
  IsNightShift: false,
  DayCross: false,
  IsRotational: false,
  IsOtApplicable: false,
  IsFixedShift: false,
};

const CHECKBOXES = [
  { name: "IsNightShift", label: "Night Shift" },
  { name: "DayCross", label: "Day Cross" },
  { name: "IsRotational", label: "Rotational" },
  { name: "IsOtApplicable", label: "OT Applicable" },
  { name: "IsFixedShift", label: "Fixed Shift" },
];

const AddShift = ({ onSuccess, onClose, isOpen }) => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
    }
  }, []);

  const resetForm = () => setForm(INITIAL_STATE);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        resetForm();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (
      !form.Name.trim() ||
      !form.Code.trim() ||
      !form.StartTime ||
      !form.EndTime
    ) {
      Swal.fire("Warning", "Please fill all required fields.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        Id: 0,
        SuperId: Number(superIdData),
        Name: form.Name.trim(),
        Code: form.Code.trim(),
        StartTime: `${form.StartTime}:00`,
        EndTime: `${form.EndTime}:00`,
        StartGrace: Number(form.StartGrace) || 0,
        EndGrace: Number(form.EndGrace) || 0,
        ApproxStartGrace: Number(form.ApproxStartGrace) || 0,
        ApproxEndGrace: Number(form.ApproxEndGrace) || 0,
        WorkMins: Number(form.WorkMins) || 0,
        OTCalcMins: Number(form.OTCalcMins) || 0,
        operdays: Number(form.operdays) || 0,
        IsNightShift: form.IsNightShift ? 1 : 0,
        DayCross: form.DayCross ? 1 : 0,
        IsRotational: form.IsRotational ? 1 : 0,
        IsOtApplicable: form.IsOtApplicable ? 1 : 0,
        IsFixedShift: form.IsFixedShift ? 1 : 0,
      };

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/shifts/createShiftType`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (data.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Shift created successfully!",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          resetForm();
          onClose();
          if (onSuccess) onSuccess();
        });
      } else {
        Swal.fire("Error", data.message || "Something went wrong!", "error");
      }
    } catch (error) {
      console.error("Error creating shift:", error);
      Swal.fire("Error", "Error occurred while creating shift.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div>
          <style>{`
            @media (max-width: 576px) {
              #offcanvasRightAddShift { width: 90% !important; }
            }
            @media (min-width: 577px) and (max-width: 992px) {
              #offcanvasRightAddShift { width: 60% !important; }
            }
            @media (min-width: 993px) and (max-width: 1440px) {
              #offcanvasRightAddShift { width: 50% !important; }
            }
            @media (min-width: 1441px) and (max-width: 2550px) {
              #offcanvasRightAddShift { width: 35% !important; }
            }
            @media (min-width: 2551px) {
              #offcanvasRightAddShift { width: 25% !important; }
            }

            /* Only the checkbox input itself turns blue when checked — not text/card */
            .shift-flag-box .form-check-input:checked {
              background-color: #0d6efd;
              border-color: #0d6efd;
            }
            .shift-flag-box .form-check-input:focus {
              box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
            }
          `}</style>

          {loading && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                zIndex: 1050,
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              }}
            >
              <Spin size="large" tip="Processing..." />
            </div>
          )}

          <div
            className="offcanvas-backdrop fade show"
            onClick={handleCancel}
          />

          <div
            className="offcanvas offcanvas-end show"
            id="offcanvasRightAddShift"
            style={{ visibility: "visible" }}
          >
            <div className="offcanvas-header">
              <h4>Add Shift</h4>
              <button
                type="button"
                className="btn-close text-reset"
                onClick={handleCancel}
                aria-label="Close"
              />
            </div>
            <hr className="m-0 p-0" />

            <div className="offcanvas-body">
              <form onSubmit={handleSave}>
                <div className="row">
                  <div className="col-4 mb-2">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span> Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. General Shift"
                      name="Name"
                      value={form.Name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. GS"
                      name="Code"
                      value={form.Code}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>Start Time
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      name="StartTime"
                      value={form.StartTime}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>End Time
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      name="EndTime"
                      value={form.EndTime}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">
                      <span className="text-danger ms-1">*</span>Operating Days
                      (1–7)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 6"
                      name="operdays"
                      value={form.operdays}
                      onChange={handleChange}
                      min={1}
                      max={7}
                      required
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">Work Minutes</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 540"
                      name="WorkMins"
                      value={form.WorkMins}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">OT Calc Minutes</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 30"
                      name="OTCalcMins"
                      value={form.OTCalcMins}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">Start Grace (mins)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 15"
                      name="StartGrace"
                      value={form.StartGrace}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">End Grace (mins)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 15"
                      name="EndGrace"
                      value={form.EndGrace}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">
                      Approx Start Grace (mins)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 10"
                      name="ApproxStartGrace"
                      value={form.ApproxStartGrace}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="col-4 mb-2">
                    <label className="form-label">
                      Approx End Grace (mins)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 10"
                      name="ApproxEndGrace"
                      value={form.ApproxEndGrace}
                      onChange={handleChange}
                      min={0}
                    />
                  </div>

                  <div className="col-12 mb-2">
                    <div className="shift-flag-box rounded border border-secondary-subtle bg-light px-3 pt-2 pb-1">
                      <div className="row">
                        {CHECKBOXES.map(({ name, label }) => (
                          <div className="col-4 mb-2" key={name}>
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="checkbox"
                                className="form-check-input m-0 flex-shrink-0"
                                id={name}
                                name={name}
                                checked={form[name]}
                                onChange={handleChange}
                                style={{ cursor: "pointer" }}
                              />
                              <label
                                className="form-check-label mb-0"
                                htmlFor={name}
                                style={{
                                  cursor: "pointer",
                                  fontSize: 13,
                                  userSelect: "none",
                                }}
                              >
                                {label}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-warning me-2"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success me-2"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AddShift.propTypes = {
  onSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default AddShift;
